/* Sorting
 *
 * - Public API
 *   - message order from public api documentation
 *   - message property order from public api documentation
 * - Internal API
 *   - message order from internal api documentation
 *   - message property order from internal api documentation
 * - Undocumented API
 *   - message order alphabetically
 *   - message property order
 *     - id properties first alphabetically
 *     - rest of properties alphabetically
 */

import {
    NinchatAttributes,
    NinchatProperties,
} from './'

export enum NinchatMessageTypes {
    // Public API
    NinchatFileMessage = 'ninchat.com/file',
    NinchatMetadataMessage = 'ninchat.com/metadata',
    NinchatNoticeMessage = 'ninchat.com/notice',
    NinchatRTCAnswerMessage = 'ninchat.com/rtc/answer',
    NinchatRTCCallMessage = 'ninchat.com/rtc/call',
    NinchatRTCIceCandidateMessage = 'ninchat.com/rtc/ice-candidate',
    NinchatRTCHangUpMessage = 'ninchat.com/rtc/hang-up',
    NinchatRTCOfferMessage = 'ninchat.com/rtc/offer',
    NinchatRTCPickUpMessage = 'ninchat.com/rtc/pick-up',
    NinchatTextMessage = 'ninchat.com/text',
    NinchatUIActionMessage = 'ninchat.com/ui/action',
    NinchatUIComposeMessage = 'ninchat.com/ui/compose',
    // Undocumented API
    NinchatDeploymentMessage = 'ninch.at/deployment',
    NinchatJsonMessage = 'ninchat.com/json',
    NinchatGithubPullRequestMessage = 'ninchat.com/github/pull_request',
    NinchatGithubPushMessage = 'ninchat.com/github/push',
    // Nonexistent API
    NinchatWritingIndicatorMessage = 'ninchat.com/writing',
}

export enum NinchatInfoMessageTypes {
    // Public API
    NinchatInfoUserMessage = 'ninchat.com/info/user',
    NinchatInfoChannelMessage = 'ninchat.com/info/channel',
    NinchatInfoJoinMessage = 'ninchat.com/info/join',
    NinchatInfoPartMessage = 'ninchat.com/info/part',
    NinchatInfoMemberMessage = 'ninchat.com/info/member',
    NinchatInfoTransferSourceMessage = 'ninchat.com/info/transfer/source',
    NinchatInfoTransferTargetMessage = 'ninchat.com/info/transfer/target',
    NinchatInfoAccessMessage = 'ninchat.com/info/access',
}

export type NinchatMessageType = NinchatMessageTypes | NinchatInfoMessageTypes | '*'

/* Messages */

/* Public API */

export interface NinchatFileMessage {
    text?: string
    files: NinchatProperties.NinchatFile[]
}

export interface NinchatInfoUserMessage {
    user_id: NinchatProperties.UserID
    user_name?: NinchatProperties.UserName
    user_name_old?: NinchatProperties.UserName
    user_deleted?: boolean
}

export interface NinchatInfoChannelMessage {
    channel_attrs_old: NinchatAttributes.NinchatChannelAttributes
    channel_attrs_new: NinchatAttributes.NinchatChannelAttributes
    cause?: NinchatProperties.EventCause
}

export interface NinchatInfoJoinMessage {
    user_id: NinchatProperties.UserID
    user_name?: NinchatProperties.UserName
    member_silenced?: boolean
    member_invitee?: boolean
}

export interface NinchatInfoPartMessage {
    user_id: NinchatProperties.UserID
    user_name?: NinchatProperties.UserName
    cause?: NinchatProperties.EventCause
}

export interface NinchatInfoMemberMessage {
    user_id: NinchatProperties.UserID
    user_name?: NinchatProperties.UserName
    member_silenced?: boolean
    member_invitee?: boolean
}

export interface NinchatInfoTransferSourceMessage {
    queue_id: NinchatProperties.QueueID
    audience_id: NinchatProperties.AudienceID
    channel_id: NinchatProperties.ChannelID
    user_id: NinchatProperties.UserID
}

export interface NinchatInfoTransferTargetMessage {
    queue_id: NinchatProperties.QueueID
    user_id: NinchatProperties.UserID
}

export interface NinchatInfoAccessMessage {
    user_id: NinchatProperties.UserID
    access_key: NinchatProperties.AccessKey
    channel_id: NinchatProperties.ChannelID
    channel_attrs: NinchatAttributes.NinchatChannelAttributes
    realm_id?: NinchatProperties.RealmID
    realm_attrs?: NinchatAttributes.NinchatRealmAttributes
    realm_member?: boolean
}

export interface NinchatMetadataMessage {
    data: NinchatProperties.Metadata
    time?: NinchatProperties.Time
    message_fold?: boolean // internal
}

export interface NinchatNoticeMessage extends NinchatTextMessage {} // eslint-disable-line @typescript-eslint/no-empty-interface

export interface NinchatRTCAnswerMessage {
    sdp: unknown // RTCSessionDescription
}

export interface NinchatRTCCallMessage {} // eslint-disable-line @typescript-eslint/no-empty-interface

export interface NinchatRTCIceCandidateMessage {
    candidate: unknown // RTCIceCandidate
}

export interface NinchatRTCHangUpMessage {} // eslint-disable-line @typescript-eslint/no-empty-interface

export interface NinchatRTCOfferMessage {
    sdp: unknown // RTCSessionDescription
}

export interface NinchatRTCPickUpMessage {
    answer: boolean
    busy?: boolean
    unsupported?: boolean
}

export interface NinchatTextMessage {
    text: string
}

export interface NinchatUIActionMessage {
    action: 'click'
    target: NinchatUIComposeMessage
}

export interface NinchatUIComposeMessage { // Array
    class?: 'full-width' | 'primary' | 'tertiary' | string
    element: 'a' | 'button' | 'select'
    href?: string
    id?: string
    label?: string
    options?: {
        label: string,
        value: string,
        selected?: boolean,
    }[]
    name?: string
}

/* Undocumented API */

export interface NinchatDeployMessage {
    title: string
}

type Commit = {
    hash: string
    message: string
    url: string
}

type Pusher = {
    name: string
}

type Repository = {
    full_name: string
    name: string
    organization: string
    url: string
}

export interface NinchatGithubPullRequestMessage {
    commits: Commit[]
    created: string
    pull_request: {
        html_url: string
        title: string
    }
    pusher: Pusher
    repository: Repository
    sender: {
        login: string
    }
}

export interface NinchatGithubPushMessage {
    commits: Commit[]
    created: string
    deleted: string
    pusher: Pusher
    ref: string
    repository: Repository
}

export interface NinchatJsonMessage {
    json: NinchatProperties.JSON
}

/* Nonexistent API */

export interface NinchatWritingIndicatorMessage {} // eslint-disable-line @typescript-eslint/no-empty-interface

/* Unions */

export type NinchatMessage =
    // Public API
    NinchatFileMessage |
    NinchatInfoUserMessage |
    NinchatInfoChannelMessage |
    NinchatInfoJoinMessage |
    NinchatInfoPartMessage |
    NinchatInfoMemberMessage |
    NinchatInfoTransferSourceMessage |
    NinchatInfoTransferTargetMessage |
    NinchatInfoAccessMessage |
    NinchatMetadataMessage |
    NinchatNoticeMessage |
    NinchatRTCAnswerMessage |
    NinchatRTCCallMessage |
    NinchatRTCIceCandidateMessage |
    NinchatRTCHangUpMessage |
    NinchatRTCOfferMessage |
    NinchatRTCPickUpMessage |
    NinchatTextMessage |
    NinchatUIActionMessage |
    NinchatUIComposeMessage |
    // Undocumented API
    NinchatDeployMessage |
    NinchatJsonMessage |
    NinchatGithubPullRequestMessage |
    NinchatGithubPushMessage |
    // Nonexistent API
    NinchatWritingIndicatorMessage

type UnionToIntersection<U> = (U extends any ? (k: U) => void : never) extends ((k: infer I) => void) ? I : never // eslint-disable-line @typescript-eslint/no-explicit-any

export type NinchatMessageProperties = Partial<UnionToIntersection<NinchatMessage>>

// vim:foldmethod=syntax
